/** zh-CN catalogue `model` (W3-09; English: src/core/model/model.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

const PROBLEM =
  '{problem, select, number {应为有限的数字，实际为 {got}} numberRange {应为 {min} 到 {max} 之间的数字，实际为 {got}} integer {应为整数，实际为 {got}} integerRange {应为 {min} 到 {max} 之间的整数，实际为 {got}} string {应为字符串，实际为 {got}} boolean {应为 true 或 false，实际为 {got}} oneOf {应为 {values} 之一，实际为 {got}} array {应为数组，实际为 {got}} tuple {应为包含 {count} 项的列表，实际为 {got}} object {应为对象，实际为 {got}} missing {缺失} unknownField {未知字段} json {应为 JSON 值（有限的数字、普通对象和数组，不能有循环），实际为 {got}} beat {应为规范化的拍数（0 <= n < d，gcd(n, d) = 1，d <= 2^32 - 1，整数部分为 i32）} triple {应为有效的 RPE 拍数三元组 [a, b, c]（c >= 1，b >= 0）} assetId {应为非空的素材 ID} trackValue {应为数字、字符串或包含 2 到 4 个数字的列表，实际为 {got}} varConst {应为数字、vec2 或 RGBA 颜色，实际为 {got}} other {{problem}，实际为 {got}}}';
const TABLE_ONE =
  '{table, select, bpm {速度点} lines {判定线} tracks {轨道} keys {关键帧} notes {音符} effects {特效} videos {视频} markers {标记} groups {分组} generators {生成器} assets {素材} other {{table}}}';
const OTHER_MANY =
  '{other, select, bpm {速度点} lines {判定线} tracks {轨道} keys {关键帧} notes {音符} effects {特效} videos {视频} markers {标记} groups {分组} generators {生成器} assets {素材} other {{other}}}';
const PROP =
  '{prop, select, moveX {X 位置} moveY {Y 位置} rotate {旋转} alpha {不透明度} speed {流速} scaleX {X 缩放} scaleY {Y 缩放} color {颜色} text {文字} incline {倾斜} paint {笔刷} gif {GIF 进度} other {{prop}}}';
const OWNER = `{ownerKind, select, line {判定线 {owner}，${PROP}，层级 {layer}} effectVar {特效 {owner}，变量“{name}”} videoVar {视频 {owner}，{which, select, alpha {不透明度} dim {暗度} other {{which}}}} other {{ownerKind} {owner}}}`;
const VALUE_TYPE =
  '{valueType, select, number {数字} rgb {RGB 颜色} string {文字} vec2 {二维向量} rgba8 {RGBA 颜色} other {{valueType}}}';

const catalog: MessageCatalog = {
  ns: 'model',
  locale: 'zh-CN',
  messages: {
    'absent.moveX': '未设置：X 位置为 0（屏幕中心）。',
    'absent.moveY': '未设置：Y 位置为 0（屏幕中心）。',
    'absent.rotate': '未设置：旋转为 0 度。',
    'absent.alpha': '未设置：判定线不可见（不透明度 0），但其音符仍会被绘制。',
    'absent.alphaUiMover': '未设置：绑定的 HUD 元素不可见。添加一个不透明度关键帧即可显示它。',
    'absent.speed': '未设置：音符停在判定线上，不会移动。',
    'absent.scaleX': '未设置：Phira 绘制完整长度的判定线。长度关键帧为 100% 时效果相同；原始值 1.0 则只有一半长。',
    'absent.scaleY': '未设置：默认粗细（6.75 px）。关键帧的值不为 1.0 时按 0.0076 x 值绘制，因此粗细会略有跳变。',
    'absent.scaleX.image': '未设置：图片按其像素尺寸绘制（100%）。',
    'absent.scaleY.image': '未设置：图片按其像素尺寸绘制（100%）。',
    'absent.scaleX.text': '未设置：文字大小 100%（54 px 字母）。',
    'absent.scaleY.text': '未设置：文字大小 100%（54 px 字母）。',
    'absent.scaleX.ui': '未设置：HUD 元素保持正常尺寸（100%）。',
    'absent.scaleY.ui': '未设置：HUD 元素保持正常尺寸（100%）。',
    'absent.scaleX.paint': '未设置：画笔保持其宽度（100%）。宽度关键帧为 100% 时效果相同；原始值 1.0 则只有一半宽。',
    'absent.scaleY.paint': '未设置：画笔保持其高度（100%）。',
    'absent.color': '未设置：判定线使用判定指示颜色（全部 Perfect 时为 Perfect 颜色）。',
    'absent.color.white': '未设置：白色。',
    'absent.text': '未设置：这不是文字线。',
    'absent.incline': '未设置：音符倾斜为 0 度。',
    'absent.paint': '未设置：这不是画笔线。',
    'absent.gif': '未设置：除非开启“自动播放”，GIF 贴图只显示第一帧。',
    'inv.field': `{where}：${PROBLEM}。`,
    'inv.idKey': `${TABLE_ONE} {id} 存储在 ID {key} 下。`,
    'inv.idSeq': `ID 计数器（{idSeq}）必须大于所有正在使用的 ID（在${TABLE_ONE}中发现了 {id}）。`,
    'inv.idShared': `ID {id} 同时被${TABLE_ONE}和${OTHER_MANY}使用。`,
    'inv.bpmEmpty': '速度表为空。',
    'inv.bpmAtZero': '必须恰好有一个速度点位于第 0 拍（发现 {count} 个）。',
    'inv.bpmBeforeZero': '速度点 {id} 位于第 0 拍之前。',
    'inv.bpmDuplicate': '速度点 {id} 和 {other} 都位于第 {beat} 拍。',
    'inv.bpmValue': '速度点 {id} 的 BPM 必须大于 0（实际为 {bpm}）。',
    'inv.orderPermutation':
      '{list, select, lineOrder {判定线顺序必须列出每条判定线，且每条恰好一次。} effectOrder {特效顺序必须列出每个特效，且每个恰好一次。} other {{list} 必须列出每条记录，且每条恰好一次。}}',
    'inv.lineParentMissing': '判定线 {id}：其父线 {parent} 不存在。',
    'inv.lineParentCycle': '判定线 {id}：沿着父线追溯会回到它自己。',
    'inv.lineGroupMissing': '判定线 {id}：分组 {group} 不存在。',
    'inv.assetMissing': `${TABLE_ONE} {id}：{field, select, texture {其贴图} hitsound {其打击音} shader {其着色器} asset {其文件} other {{field}}}引用了素材 {asset}，但它不在项目中。`,
    'inv.assetMissingMeta':
      '谱面的{field, select, music {音乐} illustration {曲绘} unlockVideo {解锁视频} other {{field}}}引用了素材 {asset}，但它不在项目中。',
    'inv.gifTrackTexture': '判定线 {id}：GIF 进度轨道需要 GIF 贴图。',
    'inv.gifAutoLoop': '判定线 {id}：“自动播放”需要 GIF 贴图，且不能有 GIF 进度轨道。',
    'inv.controlOrder':
      '判定线 {id}：{control, select, pos {位置} alpha {不透明度} size {尺寸} y {Y 偏移} other {{control}}}控制的点必须按距离排序。',
    'inv.dangling': `${TABLE_ONE} {id} 已被删除，但{what, select, note {音符 {ref}} track {轨道 {ref}} key {关键帧 {ref}} line {判定线 {ref}} effect {特效 {ref}} video {视频 {ref}} meta {谱面的{ref, select, music {音乐} illustration {曲绘} unlockVideo {解锁视频} other {{ref}}}} other {{what} {ref}}}仍在引用它。`,
    'inv.trackEmpty': '轨道 {id} 没有关键帧。',
    'inv.trackOwnerMissing': `轨道 {id}：其所有者（${OWNER}）不存在。`,
    'inv.trackOwnerShared': `轨道 {id} 和 {other} 属于同一个属性（${OWNER}）。`,
    'inv.trackLayer': `轨道 {id}：${PROP}没有层级 {layer}。`,
    'inv.trackValueType': `轨道 {id} 存放的是${VALUE_TYPE}值，但其属性需要{expected, select, number {数字} rgb {RGB 颜色} string {文字} vec2 {二维向量} rgba8 {RGBA 颜色} other {{expected}}}值。`,
    'inv.trackVarLink': `轨道 {id}：${OWNER}没有使用此轨道。`,
    'inv.trackLeadIn': '轨道 {id}：第一个关键帧必须位于第 0 拍。',
    'inv.trackImportedLeadIn': '轨道 {id}：它被标记为导入的开头，但其第一个关键帧不在第 0 拍之后。',
    'inv.keyTrackMissing': '关键帧 {id}：轨道 {track} 不存在。',
    'inv.keyOrder': '轨道 {track}：关键帧 {id} 没有晚于前一个关键帧。',
    'inv.keyFirstJump': '轨道 {track}：第一个关键帧（{id}）不能跳变；它的到达值必须等于离开值。',
    'inv.keyFirstGap': '轨道 {track}：第一个关键帧（{id}）不能开始一个空隙。',
    'inv.keyHoldJump': '轨道 {track}：关键帧 {id} 位于钩定区段之后，因此其到达值必须等于保持的值。',
    'inv.keyLastGifJump': '轨道 {track}：GIF 进度轨道的最后一个关键帧不能跳变。',
    'inv.keyValue': `关键帧 {id}：{which, select, vIn {到达值} vOut {离开值} other {{which}}}不是有效的${VALUE_TYPE}值。`,
    'inv.keyTween': '关键帧 {id}：缓动 {easing} 必须是 1 到 29，且 0 <= left < right <= 1。',
    'inv.keyBezierSpeed': '关键帧 {id}：流速不能使用贝塞尔曲线。',
    'inv.keyBezierVar': '关键帧 {id}：特效和视频变量不能使用贝塞尔曲线。',
    'inv.noteLineMissing': '音符 {id}：判定线 {line} 不存在。',
    'inv.noteHoldEnd': '音符 {id}：Hold 的结束不能早于开始。',
    'inv.noteEnd':
      '音符 {id}：{kind, select, tap {Tap} drag {Drag} flick {Flick} hold {Hold} other {{kind}}} 音符的结束必须与开始相同。',
    'inv.noteGenMissing': '音符 {id}：生成器 {gen} 不存在。',
    'inv.effectVarDuplicate': '特效 {id}：变量“{name}”被定义了两次。',
    'inv.effectVarValue':
      '特效 {id}：“{name}”的值不是有效的{type, select, float {数字} vec2 {二维向量} color {RGBA 颜色} other {{type}}}。',
    'inv.varTrack':
      '{table, select, effects {特效 {id}：变量“{name}”} videos {视频 {id}：{name, select, alpha {不透明度} dim {暗度} other {{name}}}} other {{table} {id}：{name}}}引用了轨道 {track}，但该轨道不属于它。',
    'inv.videoLineMissing': '视频 {id}：判定线 {line} 不存在。',
    'inv.groupOrder': '分组 {id} 和 {other} 的位置都是 {order}。',
    'inv.assetPath':
      '素材 {id}：“{path}”不是有效的谱面包路径（{reason, select, empty {路径为空} leadingSlash {以 / 开头} backslash {使用了 \\ 而不是 /} emptyFolder {有空的文件夹名} dotSegment {包含 . 或 ..} reserved {名称被保留} clickPng {以 click.png 结尾} builtinHitsound {是内置打击音的名称} other {{reason}}}）。',
    'inv.assetPathDuplicate': '素材 {other} 和 {id} 使用了相同的谱面包路径“{path}”。',
    'inv.previewRange': '曲目预览必须从 0 秒或之后开始，且结束晚于开始。',
    'inv.pkgRange':
      '谱面包设置{field, select, aspectRatio {宽高比} backgroundDim {背景暗度} lineLength {判定线长度} speedBakeTolPx {流速烘焙容差} rpeVersion {RPE 版本} other {{field}}}超出范围（{value}）。',
    'inv.meterOrder': '拍号标记必须按拍数递增排列，且长度为正。',
    'shape.problem': PROBLEM,
    'snapshot.notObject': '应为对象',
    'snapshot.kind': '应为“phiedit-doc”',
    'snapshot.version': '应为正整数',
    'snapshot.newer': '快照的版本为 {version}，由更新的编辑器生成（此编辑器最多读取到 {supported}）',
    'snapshot.noMigration': '没有从版本 {version} 迁移的方法',
    'snapshot.duplicateId': '重复的 ID {id}（另见 {other}）',
    'snapshot.unreadable': '无法读取',
    'txn.rejected': '此更改会破坏谱面，因此未应用：{detail}',
  },
};

export default catalog;
